import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - Wisconsin DOT Section 740 QMP Ride (2025 Standard Specifications)
// =============================================================================

const SEGMENT_LENGTH = 500; // feet - Wisconsin uses 500 ft segments

// Localized Roughness thresholds
const LR_THRESHOLD = 200; // in/mi over 25 ft continuous
const LR_BASE_LENGTH = 25; // feet

const CATEGORIES = {
  HMA_I: {
    id: 'HMA_I',
    label: 'HMA I',
    description: 'Multi-lift HMA (≥55 mph)',
    speed: 'high',
    hasIncentive: true,
    hasDisincentive: true,
    hasLR: true,
  },
  HMA_II: {
    id: 'HMA_II',
    label: 'HMA II',
    description: 'Single-lift HMA (≥55 mph)',
    speed: 'high',
    hasIncentive: true,
    hasDisincentive: true,
    hasLR: true,
  },
  PCC_II: {
    id: 'PCC_II',
    label: 'PCC II',
    description: 'Concrete (≥55 mph)',
    speed: 'high',
    hasIncentive: true,
    hasDisincentive: true,
    hasLR: true,
  },
  HMA_IV: {
    id: 'HMA_IV',
    label: 'HMA IV',
    description: 'Urban HMA (<55 mph)',
    speed: 'low',
    hasIncentive: true,
    hasDisincentive: false,
    hasLR: false,
  },
  PCC_IV: {
    id: 'PCC_IV',
    label: 'PCC IV',
    description: 'Urban PCC (<55 mph)',
    speed: 'low',
    hasIncentive: true,
    hasDisincentive: false,
    hasLR: false,
  },
  RCDG_V: {
    id: 'RCDG_V',
    label: 'RCDG V',
    description: 'Rural diamond grinding',
    speed: 'high',
    hasIncentive: true,
    hasDisincentive: false,
    hasLR: true,
  },
  UCDG_V: {
    id: 'UCDG_V',
    label: 'UCDG V',
    description: 'Urban diamond grinding',
    speed: 'low',
    hasIncentive: true,
    hasDisincentive: false,
    hasLR: true,
  },
};

// Pay adjustment formulas per 500-foot segment
// Based on Section 740 tables
const PAY_TABLES = {
  HMA_I: [
    { max: 30,       calc: () => 250,                        tier: 'incentive',    label: '< 30' },
    { max: 35,       calc: (iri) => 1750 - (50 * iri),       tier: 'incentive',    label: '30 to < 35' },
    { max: 60,       calc: () => 0,                          tier: 'base',         label: '35 to < 60' },
    { max: 75,       calc: (iri) => 1000 - (50/3 * iri),     tier: 'disincentive', label: '60 to < 75' },
    { max: Infinity, calc: () => -250,                       tier: 'disincentive', label: '≥ 75' },
  ],
  HMA_II: [
    { max: 50,       calc: () => 250,                        tier: 'incentive',    label: '< 50' },
    { max: 55,       calc: (iri) => 2750 - (50 * iri),       tier: 'incentive',    label: '50 to < 55' },
    { max: 85,       calc: () => 0,                          tier: 'base',         label: '55 to < 85' },
    { max: 100,      calc: (iri) => 4250/3 - (50/3 * iri),   tier: 'disincentive', label: '85 to < 100' },
    { max: Infinity, calc: () => -250,                       tier: 'disincentive', label: '≥ 100' },
  ],
  PCC_II: [
    { max: 50,       calc: () => 250,                        tier: 'incentive',    label: '< 50' },
    { max: 55,       calc: (iri) => 2750 - (50 * iri),       tier: 'incentive',    label: '50 to < 55' },
    { max: 85,       calc: () => 0,                          tier: 'base',         label: '55 to < 85' },
    { max: 100,      calc: (iri) => 4250/3 - (50/3 * iri),   tier: 'disincentive', label: '85 to < 100' },
    { max: Infinity, calc: () => -250,                       tier: 'disincentive', label: '≥ 100' },
  ],
  HMA_IV: [
    { max: 35,       calc: () => 250,                        tier: 'incentive',    label: '< 35' },
    { max: 45,       calc: (iri) => 1125 - (25 * iri),       tier: 'incentive',    label: '35 to < 45' },
    { max: Infinity, calc: () => 0,                          tier: 'base',         label: '≥ 45' },
  ],
  PCC_IV: [
    { max: 35,       calc: () => 250,                        tier: 'incentive',    label: '< 35' },
    { max: 45,       calc: (iri) => 1125 - (25 * iri),       tier: 'incentive',    label: '35 to < 45' },
    { max: Infinity, calc: () => 0,                          tier: 'base',         label: '≥ 45' },
  ],
  RCDG_V: [
    { max: 45,       calc: () => 125,                        tier: 'incentive',    label: '< 45' },
    { max: 55,       calc: (iri) => 687.5 - (12.5 * iri),    tier: 'incentive',    label: '45 to < 55' },
    { max: Infinity, calc: () => 0,                          tier: 'base',         label: '≥ 55' },
  ],
  UCDG_V: [
    { max: 50,       calc: () => 125,                        tier: 'incentive',    label: '< 50' },
    { max: 75,       calc: (iri) => 375 - (5 * iri),         tier: 'incentive',    label: '50 to < 75' },
    { max: Infinity, calc: () => 0,                          tier: 'base',         label: '≥ 75' },
  ],
};

// Correction requirements by category
const CORRECTION_THRESHOLDS = {
  HMA_I:  { threshold: 140, target: 60 },
  HMA_II: { threshold: 140, target: 85 },
  PCC_II: { threshold: 140, target: 85 },
  RCDG_V: { threshold: 65,  target: 65 },
  UCDG_V: { threshold: 115, target: 115 },
  // HMA_IV and PCC_IV have no correction requirements
};

const DEFAULT_SETTINGS = {
  category: 'HMA_I',
};

// =============================================================================
// CALCULATION HELPERS
// =============================================================================

const getPayAdjustment = (iri, category) => {
  const table = PAY_TABLES[category];
  if (!table) return { adjustment: 0, tier: 'base', label: '—' };
  
  for (const row of table) {
    if (iri < row.max) {
      return {
        adjustment: row.calc(iri),
        tier: row.tier,
        label: row.label,
      };
    }
  }
  
  // Fallback to last tier
  const lastRow = table[table.length - 1];
  return {
    adjustment: lastRow.calc(iri),
    tier: lastRow.tier,
    label: lastRow.label,
  };
};

const calcLRPenalty = (magnitude, lengthFt) => {
  if (magnitude <= LR_THRESHOLD) return 0;
  
  const excess = magnitude - LR_THRESHOLD;
  
  if (lengthFt <= LR_BASE_LENGTH) {
    // Length ≤ 25 ft: (roughness - 200) $/ft or $250 max
    return Math.min(excess * lengthFt, 250);
  } else {
    // Length > 25 ft: (roughness - 200) $/ft or $10/ft max
    const perFoot = Math.min(excess, 10);
    return perFoot * lengthFt;
  }
};

const getLRsInSegment = (segmentStart, segmentEnd, alrDefects) => {
  const minSta = Math.min(segmentStart, segmentEnd);
  const maxSta = Math.max(segmentStart, segmentEnd);
  
  return (alrDefects || []).filter(defect => {
    const defectStart = defect.startDistance ?? defect.distance ?? defect.station ?? 0;
    const defectStop = defect.stopDistance ?? defectStart;
    
    const defectMin = Math.min(defectStart, defectStop);
    const defectMax = Math.max(defectStart, defectStop);
    
    // Overlap check
    return defectMax >= minSta && defectMin <= maxSta;
  });
};

// =============================================================================
// MAIN CALCULATION
// =============================================================================

const calculate = (segments, alrDefects, settings, isReversed) => {
  if (!segments?.length) return null;
  
  const category = CATEGORIES[settings.category];
  const correctionReq = CORRECTION_THRESHOLDS[settings.category];
  
  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => ({
      ...seg,
      startStation: seg.from_station_ft || 0,
      stopStation: seg.to_station_ft || 0,
    }));
  
  if (validSegments.length === 0) return null;
  
  const sortedSegments = [...validSegments].sort((a, b) => 
    isReversed 
      ? b.startStation - a.startStation
      : a.startStation - b.startStation
  );
  
  let totalAdjustment = 0;
  let totalIncentive = 0;
  let totalDisincentive = 0;
  let totalLRDeduction = 0;
  let totalDistance = 0;
  let iriSum = 0;
  let correctionCount = 0;
  let totalLRCount = 0;
  
  const rows = sortedSegments.map((seg, i) => {
    // Wisconsin uses IRI (single wheel path), but we'll use the average if that's what's available
    const iri = seg.iri_avg_in_per_mi || 0;
    const segmentLength = Math.abs(seg.stopStation - seg.startStation);
    const prorationFactor = Math.min(segmentLength / SEGMENT_LENGTH, 1.0);
    const isPartialSegment = segmentLength < SEGMENT_LENGTH;
    
    // Get IRI-based pay adjustment
    const payResult = getPayAdjustment(iri, settings.category);
    const proratedAdjustment = payResult.adjustment * prorationFactor;
    
    // Check for localized roughness in this segment (if category supports it)
    let lrsInSegment = [];
    let lrPenalty = 0;
    
    if (category.hasLR) {
      const allLRsInSegment = getLRsInSegment(seg.startStation, seg.stopStation, alrDefects);
      
      // Filter to only LR events above Wisconsin's 200 in/mi threshold
      lrsInSegment = allLRsInSegment.filter(lr => (lr.magnitude || 0) > LR_THRESHOLD);
      
      // Calculate LR penalty for each qualifying defect
      lrsInSegment.forEach(lr => {
        const magnitude = lr.magnitude || 0;
        const length = lr.length || Math.abs((lr.stopDistance || 0) - (lr.startDistance || 0)) || LR_BASE_LENGTH;
        lrPenalty += calcLRPenalty(magnitude, length);
      });
      
      totalLRCount += lrsInSegment.length;
      totalLRDeduction += lrPenalty;
    }
    
    // Check if correction is required
    let requiresCorrection = false;
    if (correctionReq && iri >= correctionReq.threshold) {
      requiresCorrection = true;
      correctionCount++;
    }
    
    // Accumulate totals (only if no correction required)
    if (!requiresCorrection) {
      totalAdjustment += proratedAdjustment;
      
      if (proratedAdjustment > 0) {
        totalIncentive += proratedAdjustment;
      } else if (proratedAdjustment < 0) {
        totalDisincentive += proratedAdjustment;
      }
    }
    
    totalDistance += segmentLength;
    iriSum += iri;
    
    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;
    
    return {
      num: i + 1,
      segmentId: seg.segment_id,
      iri,
      startStation: displayStart,
      stopStation: displayStop,
      length: segmentLength,
      prorationFactor,
      isPartialSegment,
      baseAdjustment: payResult.adjustment,
      proratedAdjustment,
      tierLabel: payResult.label,
      tier: payResult.tier,
      requiresCorrection,
      lrCount: lrsInSegment.length,
      lrPenalty,
      hasIssue: requiresCorrection || payResult.tier === 'disincentive' || lrsInSegment.length > 0,
    };
  });
  
  const avgIRI = rows.length > 0 ? iriSum / rows.length : 0;
  
  // Combined total includes LR deductions
  const combinedTotal = totalAdjustment - totalLRDeduction;
  
  return {
    rows,
    totalAdjustment,
    totalIncentive,
    totalDisincentive,
    totalLRDeduction,
    combinedTotal,
    avgIRI,
    totalDistance,
    segmentCount: rows.length,
    correctionCount,
    totalLRCount,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');

const fmtStation = (ft) => {
  if (ft == null) return '—';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};

const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 2 
  });
  return v < 0 ? `-${formatted}` : formatted;
};

// =============================================================================
// COPY VALUE COMPONENT
// =============================================================================

const CopyValue = ({ label, value, displayValue, className, valueClassName }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className={cn('flex items-center justify-between py-1.5', className)}>
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className={cn('font-mono text-sm font-semibold text-gray-900', valueClassName)}>
          {displayValue ?? value}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// COPYABLE CELL COMPONENT
// =============================================================================

const CopyableCell = ({ value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <td className={cn('px-3 py-2 group', className)}>
      <div className="flex items-center gap-1">
        <span>{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </td>
  );
};

// =============================================================================
// COPYABLE COLUMN HEADER
// =============================================================================

const CopyableHeader = ({ label, data, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.join('\r\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <th className={cn('px-3 py-2 text-left group', className)}>
      <span>{label}</span>
      <button
        onClick={handleCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Copy ${label} column`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-green-500 inline" />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 inline" />
        )}
      </button>
    </th>
  );
};

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose, category }) => {
  const cat = CATEGORIES[category];
  const correctionReq = CORRECTION_THRESHOLDS[category];
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-semibold text-gray-900">
              Wisconsin DOT Section 740 Pay Tables
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-semibold text-blue-800 mb-1">Current Configuration</div>
              <div className="text-blue-700">
                {cat.label} — {cat.description}
              </div>
            </div>
            
            {/* All category tables */}
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(PAY_TABLES).map(([catId, table]) => {
                const catInfo = CATEGORIES[catId];
                const isSelected = catId === category;
                
                return (
                  <div key={catId} className={cn('border rounded', isSelected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200')}>
                    <div className={cn('px-3 py-2 border-b', isSelected ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200')}>
                      <h4 className={cn('text-sm font-semibold', isSelected ? 'text-blue-800' : 'text-gray-700')}>
                        {catInfo.label}
                        <span className="font-normal text-xs ml-2">{catInfo.description}</span>
                      </h4>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500 uppercase">
                        <tr>
                          <th className="px-2 py-1.5 text-left border-b">IRI (in/mi)</th>
                          <th className="px-2 py-1.5 text-right border-b">Adjustment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {table.map((row, i) => {
                          // Calculate sample adjustment for display
                          const prevMax = i === 0 ? 0 : table[i - 1].max;
                          const sampleIRI = row.max === Infinity 
                            ? prevMax + 10 
                            : (prevMax + row.max) / 2;
                          const sampleAdj = row.calc(sampleIRI);
                          
                          return (
                            <tr key={i} className={cn(
                              row.tier === 'incentive' && 'bg-green-50',
                              row.tier === 'disincentive' && 'bg-yellow-50'
                            )}>
                              <td className="px-2 py-1 text-gray-700">{row.label}</td>
                              <td className={cn(
                                'px-2 py-1 text-right font-mono',
                                row.tier === 'incentive' ? 'text-green-700' :
                                row.tier === 'disincentive' ? 'text-red-600' :
                                'text-gray-500'
                              )}>
                                {row.tier === 'base' ? '$0' : fmtCurrency(sampleAdj)}
                                {row.tier !== 'base' && row.max !== Infinity && i > 0 && ' (varies)'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
            
            {/* Localized Roughness */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-800 mb-1">Localized Roughness</div>
                  <div className="text-yellow-700 text-xs space-y-1">
                    <p>Threshold: {LR_THRESHOLD} in/mi over {LR_BASE_LENGTH} ft continuous</p>
                    <p>Penalty (≤25 ft): (roughness − 200) $/ft, max $250</p>
                    <p>Penalty (&gt;25 ft): min(roughness − 200, $10) × length</p>
                    <p className="italic">Note: HMA IV and PCC IV are exempt from LR penalties.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Correction Requirements */}
            {correctionReq && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-orange-800 mb-1">Correction Requirements</div>
                    <div className="text-orange-700 text-xs">
                      Segments with IRI &gt; {correctionReq.threshold} in/mi must be corrected to ≤ {correctionReq.target} in/mi.
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600 space-y-1">
              <p className="font-semibold">Reference</p>
              <p>Wisconsin DOT Standard Specifications Section 740 — QMP Ride (2025)</p>
              <p className="mt-2">
                <strong>Segment Length:</strong> 500 feet (vs 0.1 mile / 528 ft in other states)
              </p>
              <p>
                <strong>IRI:</strong> Wisconsin uses IRI per wheel path, not MRI (averaged).
              </p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function WisconsinPayAdjustment({ project, segments, alrDefects, isReversed = false }) {
  const [settings, setSettings] = useState(() => ({ 
    ...DEFAULT_SETTINGS, 
    ...project?.payAdjustment,
  }));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  
  const data = useMemo(
    () => calculate(segments, alrDefects, settings, isReversed), 
    [segments, alrDefects, settings, isReversed]
  );
  
  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(r => r.hasIssue) : data.rows;
  }, [data, issuesOnly]);
  
  const category = CATEGORIES[settings.category];
  const correctionReq = CORRECTION_THRESHOLDS[settings.category];
  
  const exportCSV = () => {
    const headers = [
      'Segment',
      'Beginning Sta.',
      'Ending Sta.',
      'Length (ft)',
      'IRI (in/mi)',
      'LR Count',
      'LR Penalty',
      'Tier',
      'Base Adjustment',
      'Prorated Adjustment',
      'Requires Correction'
    ].join(',');
    
    const csvRows = displayRows.map(r => [
      r.num,
      r.startStation.toFixed(0),
      r.stopStation.toFixed(0),
      r.length.toFixed(0),
      r.iri.toFixed(1),
      r.lrCount,
      r.lrPenalty.toFixed(2),
      r.tierLabel,
      r.baseAdjustment.toFixed(2),
      r.proratedAdjustment.toFixed(2),
      r.requiresCorrection ? 'Yes' : 'No',
    ].join(','));
    
    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `wisconsin-740-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  
  // Count issues for filter toggle
  const issueCount = data ? data.rows.filter(r => r.hasIssue).length : 0;
  
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} category={settings.category} />
      
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="mr-2">
          <h3 className="text-sm font-semibold text-gray-900">Profile Summary Sheet</h3>
          <p className="text-xs text-gray-500">Wisconsin DOT Section 740</p>
        </div>
        
        <div className="h-8 w-px bg-gray-300" />
        
        {/* Category Selector */}
        <Listbox value={settings.category} onChange={(v) => setSettings(s => ({ ...s, category: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {category.label} — {category.description}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm min-w-[280px]">
              {Object.values(CATEGORIES).map(c => (
                <ListboxOption 
                  key={c.id} 
                  value={c.id} 
                  className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}
                >
                  <span className="font-medium">{c.label}</span>
                  <span className="text-gray-500 ml-2 text-xs">{c.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        <div className="flex-1" />
        
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Table
        </button>
        
        <button 
          onClick={exportCSV} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>
      
      {/* Summary */}
      {data ? (
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-6">
            {/* Left: Localized Roughness Summary */}
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700">Localized Roughness</h4>
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                {category.hasLR ? (
                  <>
                    <CopyValue 
                      label="LR Events"
                      value={data.totalLRCount}
                      displayValue={data.totalLRCount.toString()}
                      valueClassName={data.totalLRCount > 0 ? 'text-yellow-600' : ''}
                    />
                    <CopyValue 
                      label="LR Deduction"
                      value={data.totalLRDeduction.toFixed(2)}
                      displayValue={data.totalLRDeduction > 0 ? `-${fmtCurrency(data.totalLRDeduction)}` : '$0.00'}
                      valueClassName={data.totalLRDeduction > 0 ? 'text-red-600' : ''}
                    />
                  </>
                ) : (
                  <div className="py-3 text-sm text-gray-500 italic">
                    {category.label} segments are exempt from LR penalties
                  </div>
                )}
              </div>
            </div>
            
            {/* Right: Totals */}
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Totals</h4>
                {data.correctionCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                    Correction Required
                  </span>
                )}
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                <CopyValue 
                  label="Net Adjustment"
                  value={data.combinedTotal.toFixed(2)}
                  displayValue={fmtCurrency(data.combinedTotal)}
                  valueClassName={data.combinedTotal >= 0 ? 'text-green-600' : 'text-red-600'}
                />
                <CopyValue 
                  label="Total Incentive"
                  value={data.totalIncentive.toFixed(2)}
                  displayValue={fmtCurrency(data.totalIncentive)}
                  valueClassName="text-green-600"
                />
                {category.hasDisincentive && (
                  <CopyValue 
                    label="Total Disincentive"
                    value={data.totalDisincentive.toFixed(2)}
                    displayValue={fmtCurrency(data.totalDisincentive)}
                    valueClassName={data.totalDisincentive < 0 ? 'text-red-600' : ''}
                  />
                )}
                {category.hasLR && data.totalLRDeduction > 0 && (
                  <CopyValue 
                    label="LR Deduction"
                    value={data.totalLRDeduction.toFixed(2)}
                    displayValue={`-${fmtCurrency(data.totalLRDeduction)}`}
                    valueClassName="text-red-600"
                  />
                )}
                <CopyValue 
                  label="Average IRI"
                  value={data.avgIRI.toFixed(1)}
                  displayValue={`${data.avgIRI.toFixed(1)} in/mi`}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}
      
      {/* Segments Header */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Segments {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {issueCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={issuesOnly} 
              onChange={e => setIssuesOnly(e.target.checked)} 
              className="w-3.5 h-3.5 text-blue-600 rounded" 
            />
            Show issues only ({issueCount})
          </label>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {displayRows.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12 border-r border-gray-200">#</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Beginning Sta.</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Ending Sta.</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Length (ft)</th>
                <CopyableHeader label="IRI (in/mi)" data={displayRows.map(r => r.iri.toFixed(1))} className="border-r border-gray-200" />
                {category.hasLR && (
                  <th className="pl-5 pr-3 py-2 text-left whitespace-nowrap border-r border-gray-200">LR</th>
                )}
                <CopyableHeader 
                  label="Segment Adjustment" 
                  data={displayRows.map(r => r.proratedAdjustment.toFixed(2))} 
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map(r => (
                <tr key={r.num} className={cn(
                  'hover:bg-gray-50',
                  r.requiresCorrection && 'bg-red-50',
                  r.tier === 'disincentive' && !r.requiresCorrection && 'bg-yellow-50'
                )}>
                  <td className="px-3 py-2 text-gray-400 text-xs border-r border-gray-200">{r.num}</td>
                  <CopyableCell 
                    value={r.startStation.toFixed(0)} 
                    displayValue={fmtStation(r.startStation)}
                    className="text-gray-700 font-mono text-xs border-r border-gray-200"
                  />
                  <CopyableCell 
                    value={r.stopStation.toFixed(0)} 
                    displayValue={fmtStation(r.stopStation)}
                    className="text-gray-700 font-mono text-xs border-r border-gray-200"
                  />
                  <CopyableCell 
                    value={r.length.toFixed(0)} 
                    displayValue={r.length.toFixed(0)}
                    className="text-gray-700 border-r border-gray-200"
                  />
                  <td className={cn(
                    'px-3 py-2 text-left border-r border-gray-200',
                    r.requiresCorrection ? 'text-red-600 font-semibold' : 
                    r.tier === 'disincentive' ? 'text-yellow-700' : 'text-gray-900'
                  )}>
                    {r.iri.toFixed(1)}
                  </td>
                  {category.hasLR && (
                    <td className="pl-5 pr-3 py-2 text-left border-r border-gray-200">
                      {r.lrCount > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded">
                          {r.lrCount}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-left">
                    {r.requiresCorrection ? (
                      <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                          Correct
                        </span>
                        <span className="font-mono text-gray-400 text-xs">
                          {fmtCurrency(r.proratedAdjustment)}
                        </span>
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'font-mono',
                          r.proratedAdjustment > 0 ? 'text-green-600' : 
                          r.proratedAdjustment < 0 ? 'text-red-600' : 'text-gray-500'
                        )}>
                          {fmtCurrency(r.proratedAdjustment)}
                        </span>
                        {r.isPartialSegment && (
                          <span className="text-xs text-gray-400 italic">
                            (prorated)
                          </span>
                        )}
                        {r.lrCount > 0 && (
                          <span className="text-xs text-yellow-600">
                            -{fmtCurrency(r.lrPenalty)} LR
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td className="px-3 py-2 border-r border-gray-200"></td>
                <td className="px-3 py-2 border-r border-gray-200"></td>
                <td className="px-3 py-2 text-xs text-gray-500 text-right border-r border-gray-200">Total:</td>
                <td className="px-3 py-2 font-semibold text-gray-900 border-r border-gray-200">{data.totalDistance.toFixed(0)}</td>
                <td className="px-3 py-2 text-xs text-gray-500 border-r border-gray-200">
                  <span className="mr-2">Avg:</span>
                  <span className="font-semibold text-gray-900">{data.avgIRI.toFixed(1)}</span>
                </td>
                {category.hasLR && (
                  <td className="pl-5 pr-3 py-2 text-left border-r border-gray-200">
                    {data.totalLRCount > 0 && (
                      <span className="text-xs font-semibold text-yellow-600">{data.totalLRCount}</span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2">
                  <span className={cn(
                    'font-mono font-semibold',
                    data.combinedTotal >= 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {fmtCurrency(data.combinedTotal)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <p>{issuesOnly ? 'No segments with issues' : 'No segment data available'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>Wisconsin DOT Section 740 (2025)</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Reverse Stationing</span>
          )}
        </div>
        <span>
          {category.label} — 
          {correctionReq 
            ? ` Correction threshold: >${correctionReq.threshold} in/mi` 
            : ' No correction requirement'
          }
        </span>
      </div>
    </div>
  );
}